import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import POCGrid, { emptyPOC } from "@/components/ra/POCGrid";
import FileManager from "@/components/files/FileManager";
import {
  Plus, Search, Send, MessageSquare, Lock, AlertTriangle, ArrowUpCircle, Users, Star,
  BookmarkPlus, Trash2,
} from "lucide-react";

const PRESETS = {
  default: { title: "Research Leads", subtitle: "Companies and contacts you are researching", status: null },
  outreach: { title: "Outreach", subtitle: "Leads where outreach is in progress", status: "outreach_sent,waiting_response" },
  replies: { title: "Replies", subtitle: "Leads that have received a reply", status: "replied" },
  submitted: { title: "Submitted for Review", subtitle: "Leads submitted to Admin for review", status: "admin_review,positive,negative,future_reference,do_not_contact,bdm_assigned,returned" },
};

const EMPTY = { company: "", position_title: "", location: "", region: "US", company_domain: "", company_linkedin: "", industry: "", job_posting_url: "", notes: "" };

export default function RALeads({ preset = "default" }) {
  const cfg = PRESETS[preset] || PRESETS.default;
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pocs, setPocs] = useState([{ ...emptyPOC(), is_primary: true }]);
  const [editId, setEditId] = useState(null);
  const [dup, setDup] = useState(null);
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null);
  const [msg, setMsg] = useState({ subject: "", body: "", poc_id: "" });
  const [taxonomy, setTaxonomy] = useState({ industries: [], job_titles: [] });
  const [savedViews, setSavedViews] = useState([]);
  const [filters, setFilters] = useState({ industry: "all", region: "all", q: "" });
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");

  const load = useCallback(async () => {
    try {
      const params = {
        ...(cfg.status ? { status: cfg.status } : {}),
        ...(filters.industry !== "all" ? { industry: filters.industry } : {}),
        ...(filters.region !== "all" ? { region: filters.region } : {}),
        ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
      };
      const { data } = await api.get("/ra/leads", { params });
      setItems(data.items);
    } catch (e) { toast.error(apiErr(e)); setItems([]); }
  }, [cfg.status, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/ra/taxonomy").then(({ data }) => setTaxonomy(data)).catch(() => {});
    api.get("/ra/saved-views").then(({ data }) => setSavedViews(data.items)).catch(() => {});
  }, []);

  const checkDup = async (f) => {
    if (!f.company || !f.position_title) { setDup(null); return; }
    try { const { data } = await api.post("/ra/leads/duplicate-check", { company: f.company, position_title: f.position_title, location: f.location }); setDup(data); }
    catch { setDup(null); }
  };

  const openCreate = () => { setForm(EMPTY); setPocs([{ ...emptyPOC(), is_primary: true }]); setEditId(null); setDup(null); setCreateOpen(true); };

  const openEdit = (lead) => {
    setForm({ company: lead.company, position_title: lead.position_title, location: lead.location || "", region: lead.region || "US",
      company_domain: lead.company_domain || "", company_linkedin: lead.company_linkedin || "", industry: lead.industry || "",
      job_posting_url: lead.job_posting_url || "", notes: lead.notes || "" });
    setPocs((lead.pocs && lead.pocs.length ? lead.pocs : [{ ...emptyPOC(), is_primary: true }]).map((p) => ({ ...p })));
    setEditId(lead.id); setDup(null); setCreateOpen(true);
  };

  const save = async () => {
    if (!form.company || !form.position_title) { toast.error("Company and position are required"); return; }
    const cleanPocs = pocs.filter((p) => (p.name || "").trim() || (p.email || "").trim());
    if (!cleanPocs.length) { toast.error("Add at least one POC"); return; }
    const payload = { ...form, pocs: cleanPocs };
    try {
      if (editId) await api.put(`/ra/leads/${editId}`, payload);
      else await api.post("/ra/leads", payload);
      toast.success(editId ? "Lead updated" : "Research lead created");
      setCreateOpen(false); load();
      if (detail && editId === detail.lead.id) openDetail(editId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || "Duplicate/restricted") : apiErr(e));
    }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/ra/leads/${id}`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const startAction = (type) => {
    const first = (detail.lead.pocs || [])[0];
    setMsg({ subject: "", body: "", poc_id: first?.id || "" });
    setAction(type);
  };

  const doAction = async () => {
    const id = detail.lead.id;
    try {
      const body = { subject: msg.subject, body: msg.body, poc_id: msg.poc_id };
      if (action === "outreach") await api.post(`/ra/leads/${id}/outreach`, { channel: "email", ...body });
      else await api.post(`/ra/leads/${id}/reply`, { ...body });
      toast.success(action === "outreach" ? "Outreach logged" : "Reply logged");
      setAction(null); openDetail(id); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitReview = async () => {
    const id = detail.lead.id;
    try { await api.post(`/ra/leads/${id}/submit-review`); toast.success("Submitted for Admin review"); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const applySavedView = (viewId) => {
    if (viewId === "none") {
      setFilters({ industry: "all", region: "all", q: "" });
      return;
    }
    const view = savedViews.find((item) => item.id === viewId);
    if (!view) return;
    setFilters({
      industry: view.filters.industry || "all",
      region: view.filters.region || "all",
      q: view.filters.q || "",
    });
  };

  const saveView = async () => {
    if (!saveViewName.trim()) {
      toast.error("Enter a saved view name");
      return;
    }
    try {
      const { data } = await api.post("/ra/saved-views", {
        name: saveViewName.trim(),
        filters: {
          ...(cfg.status ? { status: cfg.status } : {}),
          ...(filters.industry !== "all" ? { industry: filters.industry } : {}),
          ...(filters.region !== "all" ? { region: filters.region } : {}),
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
        },
      });
      setSavedViews((current) => [data, ...current]);
      setSaveViewName("");
      setSaveViewOpen(false);
      toast.success("Saved view created");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const deleteView = async (viewId) => {
    try {
      await api.delete(`/ra/saved-views/${viewId}`);
      setSavedViews((current) => current.filter((view) => view.id !== viewId));
      toast.success("Saved view removed");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const lockedForRa = detail && ["admin_review", "positive", "negative", "future_reference", "do_not_contact", "bdm_assigned"].includes(detail.lead.status);

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle}
        actions={preset === "default" && <Button onClick={openCreate} data-testid="ra-create-lead-btn" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Plus className="h-4 w-4" /> New Research Lead</Button>} />

      <Card className="p-3 mb-4" data-testid="ra-lead-view-controls">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search company or title"
              data-testid="ra-leads-search"
              className="pl-9"
            />
          </div>
          <Select
            value={filters.industry}
            onValueChange={(industry) => setFilters({ ...filters, industry })}
          >
            <SelectTrigger data-testid="ra-leads-industry-filter"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All industries</SelectItem>
              {taxonomy.industries.map((industry) => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.region} onValueChange={(region) => setFilters({ ...filters, region })}>
            <SelectTrigger data-testid="ra-leads-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {["US", "CA", "GCC", "IN"].map((region) => (
                <SelectItem key={region} value={region}>{region}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select onValueChange={applySavedView} defaultValue="none">
            <SelectTrigger data-testid="ra-saved-view-select"><SelectValue placeholder="Saved views" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Clear view filters</SelectItem>
              {savedViews.map((view) => <SelectItem key={view.id} value={view.id}>{view.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setSaveViewOpen(true)}
            data-testid="ra-save-view-button"
            className="gap-1.5"
          >
            <BookmarkPlus className="h-4 w-4" /> Save View
          </Button>
        </div>
        {savedViews.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {savedViews.map((view) => (
              <button
                key={view.id}
                onClick={() => deleteView(view.id)}
                data-testid={`ra-delete-view-${view.id}`}
                className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-muted-foreground hover:text-destructive"
                title={`Delete saved view ${view.name}`}
              >
                {view.name}<Trash2 className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {items === null ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <EmptyState icon={Search} title="No leads here" description="Create a research lead or check another view." />
      ) : (
        <Card className="overflow-hidden" data-testid="ra-leads-list">
          <div className="divide-y divide-border">
            {items.map((l) => (
              <div key={l.id} className="flex items-center gap-3 p-3.5 cursor-pointer hover:bg-accent/40" data-testid={`ra-lead-${l.id}`} onClick={() => openDetail(l.id)}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.company}</div>
                  <div className="text-xs text-muted-foreground">{l.position_title} · {l.location || "—"} · {l.region}</div>
                </div>
                <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" />{(l.pocs || []).length}</span>
                <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Send className="h-3.5 w-3.5" />{l.outreach_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{l.reply_count || 0}</span>
                </div>
                {l.is_locked && <Lock className="h-3.5 w-3.5 text-amber-600" />}
                <StatusBadge value={l.status} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Create / Edit lead */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{editId ? "Edit Research Lead" : "New Research Lead"}</DialogTitle><DialogDescription>Company details + multiple Points of Contact. Duplicate checks run automatically.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[68vh] overflow-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Company *" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} onBlur={() => checkDup(form)} data-testid="lead-company" />
              <Input list="ra-lead-title-taxonomy" placeholder="Open position *" value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} onBlur={() => checkDup(form)} data-testid="lead-position" />
            </div>
            <datalist id="ra-lead-title-taxonomy">
              {taxonomy.job_titles.map((title) => <option key={title} value={title} />)}
            </datalist>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Job location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} onBlur={() => checkDup(form)} data-testid="lead-location" />
              <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["US", "CA", "GCC", "IN"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.industry || "unspecified"} onValueChange={(value) => setForm({ ...form, industry: value === "unspecified" ? "" : value })}>
                <SelectTrigger data-testid="lead-industry-select"><SelectValue placeholder="Industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unspecified">Industry not specified</SelectItem>
                  {taxonomy.industries.map((industry) => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Company website" value={form.company_domain} onChange={(e) => setForm({ ...form, company_domain: e.target.value })} />
              <Input placeholder="Company LinkedIn" value={form.company_linkedin} onChange={(e) => setForm({ ...form, company_linkedin: e.target.value })} />
              <Input placeholder="Job posting URL" value={form.job_posting_url} onChange={(e) => setForm({ ...form, job_posting_url: e.target.value })} />
            </div>
            {dup && (dup.is_duplicate || dup.is_restricted) && (
              <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 rounded-lg p-2.5" data-testid="lead-dup-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  {dup.is_duplicate && <div>Duplicate within {dup.window_days} days (status: {dup.existing_status}). Available on {dup.available_on || "—"}.</div>}
                  {dup.is_restricted && <div>Company is restricted ({dup.restriction_status}). Admin override required.</div>}
                </div>
              </div>
            )}
            <POCGrid pocs={pocs} setPocs={setPocs} />
            <Textarea placeholder="General notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="lead-create-submit" className="bg-[#255058] hover:bg-[#1c3d43] text-white">{editId ? "Save Lead" : "Create Lead"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save research view</DialogTitle>
            <DialogDescription>Save these filters for your own R.A. workspace.</DialogDescription>
          </DialogHeader>
          <Input
            value={saveViewName}
            onChange={(e) => setSaveViewName(e.target.value)}
            placeholder="e.g. US Technology targets"
            data-testid="ra-saved-view-name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveViewOpen(false)} data-testid="ra-saved-view-cancel">Cancel</Button>
            <Button onClick={saveView} data-testid="ra-saved-view-submit" className="bg-[#255058] text-white hover:bg-[#1c3d43]">Save view</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setAction(null); } }}>
        <DialogContent className="max-w-3xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{detail.lead.company} <StatusBadge value={detail.lead.status} /></DialogTitle>
                <DialogDescription>{detail.lead.position_title} · {detail.lead.location || "—"} · {detail.lead.region}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[68vh] overflow-auto pr-1 space-y-4">
                {detail.lead.returned_reason && detail.lead.status === "returned" && (
                  <div className="text-xs text-amber-800 bg-amber-50 rounded-lg p-2.5">Returned by Admin: {detail.lead.returned_reason}</div>
                )}

                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Users className="h-4 w-4 text-[#255058]" /> Points of Contact ({(detail.lead.pocs || []).length})</div>
                  <div className="border border-border rounded-lg divide-y divide-border" data-testid="detail-poc-list">
                    {(detail.lead.pocs || []).map((p) => (
                      <div key={p.id} className="flex items-center gap-2 p-2.5 text-sm">
                        {p.is_primary && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500 shrink-0" />}
                        <span className="font-medium">{p.name}</span>
                        <span className="text-muted-foreground text-xs">{p.title || "—"}{p.email ? ` · ${p.email}` : ""}</span>
                        <span className="ml-auto text-xs text-muted-foreground flex items-center gap-2"><Send className="h-3 w-3" />{p.outreach_count || 0} <MessageSquare className="h-3 w-3" />{p.reply_count || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 max-h-44 overflow-auto">
                  {detail.outreach.map((o) => (
                    <div key={o.id} className="text-sm border-l-2 border-sky-400 pl-3"><div className="flex items-center gap-1.5 font-medium"><Send className="h-3.5 w-3.5 text-sky-500" />{o.subject || "Outreach"}{o.poc_name ? <span className="text-xs text-muted-foreground font-normal">→ {o.poc_name}</span> : null}</div><div className="text-muted-foreground text-xs">{o.body}</div></div>
                  ))}
                  {detail.replies.map((r) => (
                    <div key={r.id} className="text-sm border-l-2 border-violet-400 pl-3"><div className="flex items-center gap-1.5 font-medium"><MessageSquare className="h-3.5 w-3.5 text-violet-500" />{r.subject || "Reply"}{r.poc_name ? <span className="text-xs text-muted-foreground font-normal">from {r.poc_name}</span> : null}</div><div className="text-muted-foreground text-xs">{r.body}</div></div>
                  ))}
                  {!detail.outreach.length && !detail.replies.length && <div className="text-sm text-muted-foreground">No outreach or replies logged yet.</div>}
                </div>

                {!lockedForRa && (
                  <FileManager recordType="ra_lead" recordId={detail.lead.id} canWrite={can("ra:lead_manage")} title="Evidence & Attachments" />
                )}

                {action && (
                  <div className="space-y-2 border-t border-border pt-3">
                    <Select value={msg.poc_id} onValueChange={(v) => setMsg({ ...msg, poc_id: v })}>
                      <SelectTrigger data-testid="ra-msg-poc"><SelectValue placeholder="Select POC" /></SelectTrigger>
                      <SelectContent>{(detail.lead.pocs || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_primary ? " (Primary)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input placeholder="Subject" value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} data-testid="ra-msg-subject" />
                    <Textarea placeholder={action === "outreach" ? "Outreach message… (recorded manually — NEXREQ does not send email)" : "Reply content… (recorded manually)"} value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} data-testid="ra-msg-body" />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setAction(null)}>Cancel</Button>
                      <Button size="sm" onClick={doAction} disabled={!msg.body || !msg.poc_id} data-testid="ra-msg-save" className="bg-[#255058] hover:bg-[#1c3d43] text-white">Save</Button>
                    </div>
                  </div>
                )}
              </div>

              {!action && (lockedForRa ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground border-t border-border pt-3"><Lock className="h-4 w-4 text-amber-600" /> This lead is under Admin review and is read-only.</div>
              ) : (
                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(detail.lead)} data-testid="ra-edit-lead">Edit / POCs</Button>
                  <Button variant="outline" size="sm" onClick={() => startAction("outreach")} data-testid="ra-log-outreach" className="gap-1.5"><Send className="h-4 w-4" /> Log Outreach</Button>
                  <Button variant="outline" size="sm" onClick={() => startAction("reply")} data-testid="ra-log-reply" className="gap-1.5"><MessageSquare className="h-4 w-4" /> Log Reply</Button>
                  <Button size="sm" onClick={submitReview} data-testid="ra-submit-review" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><ArrowUpCircle className="h-4 w-4" /> Submit for Review</Button>
                </DialogFooter>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
